/**
 * Deliver → Notifications — org-level control center for outgoing alerts.
 * Trainers (with notifications.view/manage) decide which alerts fire, who
 * receives them, and how often. Email HTML templates stay in Settings.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { SkeletonCard } from '../../components/common/SkeletonCard';
import { Bell, ClipboardList, SlidersHorizontal, FileText, ShieldAlert } from 'lucide-react';
import { RuleCard } from './RuleCard';
import { DeliveryLogTab } from './DeliveryLogTab';
import { TemplatesTab } from './TemplatesTab';

export default function NotificationCenterPage() {
  const { get } = useApi();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState(null);
  const canView = hasPermission('notifications.view') || hasPermission('notifications.manage');
  const canManage = hasPermission('notifications.manage');

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await get('/notification-rules');
        setRules(res?.rules || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load notification rules');
        setRules([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const staffRules = (rules || []).filter(r => r.audience === 'staff');
  const traineeRules = (rules || []).filter(r => r.audience === 'trainee');

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="notifications-access-denied">
        <ShieldAlert className="w-10 h-10 text-muted-foreground/50 mb-3" />
        <h1 className="text-lg font-semibold">No access to Notifications</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Ask your org admin to grant the “Notifications” permission in Settings → Roles &amp; Permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-center-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--lumyn-content)] font-semibold">
            <Bell className="w-4 h-4" /> Deliver
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Control which alerts go out, who receives them, and how often.
            Digest modes roll multiple alerts into one summary email per recipient.
          </p>
        </div>
        <div className="inline-flex rounded-lg border bg-muted/40 p-1 self-start">
          <TabPill active={tab === 'rules'} onClick={() => setTab('rules')} icon={SlidersHorizontal} label="Alert rules" testid="notif-center-tab-rules" />
          {canManage && (
            <TabPill active={tab === 'templates'} onClick={() => setTab('templates')} icon={FileText} label="Templates" testid="notif-center-tab-templates" />
          )}
          <TabPill active={tab === 'log'} onClick={() => setTab('log')} icon={ClipboardList} label="Delivery log" testid="notif-center-tab-log" />
        </div>
      </div>

      {tab === 'log' ? (
        <DeliveryLogTab rules={rules || []} />
      ) : tab === 'templates' ? (
        <TemplatesTab />
      ) : rules === null ? (
        <div className="grid gap-4 md:grid-cols-2"><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">Team alerts</h2>
              <p className="text-xs text-muted-foreground">Sent to trainers and admins when trainees need attention.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="staff-rules-grid">
              {staffRules.map(r => (
                <RuleCard key={r.key} rule={r} canManage={canManage}
                  onSaved={(updated) => setRules(prev => prev.map(p => p.key === updated.key ? { ...p, ...updated } : p))} />
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-base font-semibold">Trainee emails</h2>
              <p className="text-xs text-muted-foreground">Sent directly to trainees. Toggle off to stop the emails (in-app notifications stay on).</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="trainee-rules-grid">
              {traineeRules.map(r => (
                <RuleCard key={r.key} rule={r} canManage={canManage}
                  onSaved={(updated) => setRules(prev => prev.map(p => p.key === updated.key ? { ...p, ...updated } : p))} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function TabPill({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
        active ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
